import React, { useCallback } from 'react';
import { useTranslator } from '@jutro/locale';
import moment from 'moment';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { MLLineTypeUtil, MLQuoteNameUtil, MLProductCodeUtil } from 'ml-portals-util-js';
import PropTypes from 'prop-types';
import metadata from './MLAgentPolicySummaryHeaderComponent.metadata.json5';
import messages from './MLAgentPolicySummaryHeaderComponent.messages.js';


function MLAgentPolicySummaryHeaderComponent(props) {
    const translator = useTranslator();
    const {
        model: policyDetails,
        isNamedNonOwner,isModernProduct } = props;
    const quoteId = _.get(policyDetails, 'quoteId.value', '');
    const policyId = _.get(policyDetails, 'policyId.value', '');
    const TERM_TYPE_HALF_YEAR = 'HalfYear';
    const TERM_TYPE_7_DAY = '7Days';
    const effectiveDate = _.get(policyDetails, 'effective_ML.value', '');
    const expirationDate = _.get(policyDetails, 'expiration_ML.value', '');

    const isPersonalAuto = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isPersonalAuto(line));
    };

    const isHomeOwners = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isHomeOwners(line));
    };

    const isPersonalUmbrella = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isPersonalUmbrella(line));
    };


    const isLandlordHome = () => {
        return MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(policyDetails.coveragePartType);
    };

    const isLandlordCondo = () => {
        return MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(policyDetails.coveragePartType);
    };
    const getPolicyIconCLassName = () => {
        if (isPersonalAuto(policyDetails)) {
            return 'pull-left icon-icon-car-white icon-lg margin-right-20'
        }
        if (isHomeOwners(policyDetails)) {
            return 'pull-left icon-ml-home-detailed-icon icon-lg margin-right-20'
        }
        if (isPersonalUmbrella(policyDetails)) {
            return 'pull-left icon-foremost-umbrella-icon icon-lg margin-right-20'
        }
    }
    const getPolicyCoverageType = () => {
        if (isPersonalUmbrella(policyDetails)) {
            return translator(messages.umbrella);
        }

        const lobs = _.get(policyDetails, 'lobs.value', undefined);
        const lobsPolChange = _.get(policyDetails, 'lobsPolChange.value', undefined);
        if (!_.isUndefined(lobs)) {
            return lobs.personalAuto ? translator(messages.auto)
                : MLQuoteNameUtil.getCoveragePartTypeName(policyDetails.coveragePartType.value.code);
        }
        if (!_.isUndefined(lobsPolChange)) {
            return lobsPolChange.personalAuto ? translator(messages.auto)
                : MLQuoteNameUtil.getCoveragePartTypeName(policyDetails.coveragePartType.value.code);
        }
    };


    const getPerTermLabel = (selectedTermType) => {
        let label = translator(messages.perTermAnnual);
        if (selectedTermType === TERM_TYPE_HALF_YEAR) {
            label = translator(messages.perTerm6Month);
        } else if (selectedTermType === TERM_TYPE_7_DAY) {
            label = translator(messages.perTerm7day);
        }
        return label;
    }

    const getNumberOfVehicles = useCallback(() => {
        if (policyDetails.vehicles_ML && policyDetails.vehicles_ML.length) {
            return policyDetails.vehicles_ML.length;
        }
        return 0;
    }, [policyDetails]);

    const convertLocalDateToDate = (localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    };

   const getLabelForNamedNonOwner=()=>{
    return isModernProduct ? translator(messages.namedNonOwnerHeadingNP)
            : translator(messages.namedNonOwnerHeading);
   }

    const overrideProps = {
        policyIconClassName: {
            className: getPolicyIconCLassName()
        },
        policytype: {
            content: translator(messages.insurancetype, { type: getPolicyCoverageType() })
        },
        quoteNumber: {
            content: translator(messages.quoteX, { quoteId: quoteId }),
            visible: !_.isNil(policyDetails.quoteId.value)
        },
        policyNumber: {
            content: translator(messages.policyX, { policyId: policyId }),
            visible: !_.isNil(policyDetails.policyId.value)
        },
        offeringName: {
            visible: !isModernProduct && policyDetails.offeringName_ML.value && !isLandlordHome() && !isLandlordCondo(),
            content: policyDetails.offeringName_ML.value
        },
        costHeaderSpan: {
            value: _.get(policyDetails, 'fullTermCost_Ext.value.amount'),
            currency: _.get(policyDetails, 'fullTermCost_Ext.value.currency'),
            showFractions: true
        },
        termTypeSpan: {
            content: ` ${getPerTermLabel(policyDetails.termType_ML.value)}`
        },
        policySummaryOwnerContainer: {
            visible: isPersonalAuto(policyDetails),
            content:policyDetails.vehicles_ML===0 && getLabelForNamedNonOwner()
        },
        NamedNonowner: {
            visible: isNamedNonOwner,
            content:getLabelForNamedNonOwner()
        },
        noOfVehicles: {
            visible: !isNamedNonOwner,
            content: `${getNumberOfVehicles()} ${getNumberOfVehicles() > 1 ? 'Vehicles' : 'Vehicle'}`
        },
        policySummaryAddressContainer: {
            visible: !isPersonalAuto(policyDetails) && policyDetails.policyAddress.value
        },
        stateFeesComponent: {
            visible: policyDetails.totalFullTermChargesRPT_Ext.amount.value !== 0,
            totalFees: policyDetails.totalFullTermChargesRPT_Ext.value,
            itemizedFees: policyDetails.fullTermFeesCosts_Ext.value,
            displayAlternativeTemplate: false
        },
        addressLine1: {
            content: policyDetails.policyAddress.addressLine1?.value

        },
        addressLine2: {
            content: policyDetails.policyAddress.addressLine2?.value

        },
        addressLine3: {
            conent: policyDetails.policyAddress.addressLine3?.value

        },
        cityStateZip: {
            content: `${policyDetails.policyAddress.city?.value}, ${policyDetails.policyAddress.state?.value.code} ${policyDetails.policyAddress.postalCode?.value}`

        },
        effectiveDate: {
            content: `${translator(messages.effectiveDate)} ${convertLocalDateToDate(effectiveDate, true)}`
        },
        expirationDate: {
            content: `${translator(messages.expirationDate)} ${convertLocalDateToDate(expirationDate, true)}`
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={policyDetails}
        />
    );
}

MLAgentPolicySummaryHeaderComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    isNamedNonOwner: PropTypes.bool

};

MLAgentPolicySummaryHeaderComponent.defaultProps = {
    isNamedNonOwner: false

};

export default MLAgentPolicySummaryHeaderComponent;